import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DownloadSimple, FilePdf, FileXls, CaretDown } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";

/**
 * ExportButton — downloads a file from a backend endpoint that returns a stream.
 *
 * Props:
 *  - endpoint:      { pdf: "/export/…pdf", xlsx: "/export/…xlsx" }  (relative to /api)
 *  - params:        query object (optional)
 *  - filenameBase:  used for the downloaded file name (extension auto-added)
 *  - label:         button label (default "Download")
 *  - testId:        base data-testid (buttons will use `${testId}-pdf` / `${testId}-xlsx`)
 *  - variant:       "primary" | "outline" (default outline)
 */
export default function ExportButton({ endpoint, params = {}, filenameBase = "export", label = "Download", testId = "export", variant = "outline", disabled = false, icon = null, pdfIcon = null, xlsxIcon = null, iconOnly = false, tooltip = null, expectedCount = null, verifyCount = null }) {
    const [busy, setBusy] = useState(false);

    const download = async (kind) => {
        setBusy(true);
        try {
            // v1.29.60 — Give any in-flight DB write (e.g. a payment the admin
            // just added) 500ms to commit before we pull the export, so the
            // freshly-added row is never missed.
            await new Promise((r) => setTimeout(r, 500));
            // Verify the backend's grouped row count matches what's on screen;
            // if the DB hasn't caught up yet, re-fetch once before generating.
            if (verifyCount && expectedCount != null) {
                try {
                    const c = await verifyCount();
                    if (c != null && c !== expectedCount) {
                        await new Promise((r) => setTimeout(r, 400));
                        await verifyCount();
                    }
                } catch { /* verification is best-effort — never block the download */ }
            }
            const url = endpoint[kind];
            // Cache-bust (unique _ts + no-cache headers) so a browser/proxy
            // never serves a stale PDF/XLSX for the identical GET URL — the
            // root cause of the "PDF missing the last row" report.
            const res = await api.get(url, {
                params: { ...params, _ts: Date.now() },
                responseType: "blob",
                headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
            });
            const blob = new Blob([res.data], {
                type: kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            dl.download = withDatePrefix(`${filenameBase}.${kind}`);
            document.body.appendChild(dl);
            dl.click();
            document.body.removeChild(dl);
            URL.revokeObjectURL(dl.href);
            toast.success(`${kind.toUpperCase()} downloaded`);
        } catch (e) {
            toast.error(`Failed to download ${kind.toUpperCase()}`);
        } finally { setBusy(false); }
    };

    const triggerBtn = (
        <DropdownMenuTrigger asChild>
            <Button
                type="button"
                variant={variant === "primary" ? "default" : "outline"}
                disabled={busy || disabled}
                data-testid={testId}
                aria-label={iconOnly ? (tooltip || label) : undefined}
                className={variant === "primary" ? "bg-yellow-600 text-white hover:bg-yellow-700" : ""}
            >
                {icon || <DownloadSimple size={16} className={iconOnly ? "" : "mr-2"} />}
                {!iconOnly && (busy ? "Preparing…" : label)}
                <CaretDown size={12} className={iconOnly ? "ml-1 opacity-70" : "ml-2 opacity-70"} />
            </Button>
        </DropdownMenuTrigger>
    );

    return (
        <DropdownMenu>
            {tooltip ? (
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>{triggerBtn}</TooltipTrigger>
                        <TooltipContent>{tooltip}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : triggerBtn}
            <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem data-testid={`${testId}-pdf`} onClick={() => download("pdf")} className="cursor-pointer">
                    {pdfIcon || <FilePdf size={16} weight="fill" className="mr-2 text-rose-600" />} PDF
                </DropdownMenuItem>
                <DropdownMenuItem data-testid={`${testId}-xlsx`} onClick={() => download("xlsx")} className="cursor-pointer">
                    {xlsxIcon || <FileXls size={16} weight="fill" className="mr-2 text-emerald-600" />} Excel
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
